import { useState } from 'react';
import {
    autoUpdate,
    flip,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useHover,
    useInteractions,
    useRole,
    useTransitionStyles,
} from '@floating-ui/react';

interface UseFloatingMenuOptions {
    // Floating UI options
    placement?: 'bottom' | 'top' | 'left' | 'right' | 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
    offsetValue?: number;
    keepOpen?: boolean;

    // Trigger options
    trigger?: 'click' | 'hover';
    hoverDelay?: number; // Delay in ms before hiding on hover (only used for hover trigger)

    // Animation options
    duration?: number;
    slideDistance?: number;
    scaleAmount?: number;
    slideDirection?: 'left' | 'right' | 'top' | 'bottom';
    transformOrigin?: 'top' | 'bottom' | 'left' | 'right';
    // Analytics
    name?: string;
    id?: string;
}

interface UseFloatingMenuReturn {
    // State
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;

    // Floating UI
    refs: ReturnType<typeof useFloating>['refs'];
    floatingStyles: ReturnType<typeof useFloating>['floatingStyles'];
    context: ReturnType<typeof useFloating>['context'];
    getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps'];
    getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps'];

    // Transition
    transitionStyles: React.CSSProperties;

    // Actions
    closeMenu: () => void;
}

export const useFloatingMenu = (options: UseFloatingMenuOptions = {}): UseFloatingMenuReturn => {
    const {
        placement = 'bottom',
        offsetValue = 8,
        keepOpen = false,
        trigger = 'click',
        hoverDelay = 100,
        duration = 150,
        name: _name,
        id: _id,
    } = options;

    const [isOpen, setIsOpen] = useState(false);

    // Floating UI setup
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: open => {
            setIsOpen(open);
        },
        middleware: [
            offset(offsetValue),
            flip({
                fallbackAxisSideDirection: 'end',
            }),
            shift({ padding: 5 }),
        ],
        whileElementsMounted: autoUpdate,
        placement: placement,
    });

    // Setup interactions based on trigger type
    const click = useClick(context, { enabled: trigger === 'click' });
    const dismiss = useDismiss(context, { enabled: trigger === 'click' });
    const hover = useHover(context, {
        enabled: trigger === 'hover',
        delay: { close: hoverDelay },
    });
    const role = useRole(context);

    const { getReferenceProps: originalGetReferenceProps, getFloatingProps } = useInteractions([
        click,
        dismiss,
        hover,
        role,
    ]);

    // Wrap getReferenceProps to add preventDefault and stopPropagation at the lowest level
    const getReferenceProps: typeof originalGetReferenceProps = userProps => {
        const props = originalGetReferenceProps(userProps);
        const originalOnClick = props.onClick;
        const originalOnPointerDown = props.onPointerDown;
        const originalOnMouseDown = props.onMouseDown;

        return {
            ...props,
            onPointerDown: (e: React.PointerEvent) => {
                e.preventDefault();
                e.stopPropagation();
                if (typeof originalOnPointerDown === 'function') {
                    originalOnPointerDown(e);
                }
            },
            onMouseDown: (e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                if (typeof originalOnMouseDown === 'function') {
                    originalOnMouseDown(e);
                }
            },
            onClick: (e: React.MouseEvent) => {
                e.stopPropagation();
                if (typeof originalOnClick === 'function') {
                    originalOnClick(e);
                }
            },
        };
    };

    // Transition animation with configurable direction
    const { styles: transitionStyles } = useTransitionStyles(context, {
        duration,
        initial: {
            opacity: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
        },
        open: {
            opacity: 1,
            visibility: 'visible',
            pointerEvents: 'auto',
        },
        close: {
            opacity: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
        },
    });

    const closeMenu = () => {
        if (!keepOpen) {
            setIsOpen(false);
        }
    };

    return {
        isOpen,
        setIsOpen,
        refs,
        floatingStyles,
        context,
        getReferenceProps,
        getFloatingProps,
        transitionStyles,
        closeMenu,
    };
};
